import React, { useState } from 'react';
import {
    Box,
    Typography,
    Grid,
    Paper,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Chip,
    Card,
    CardContent,
    Link,
    Button,
    Avatar
} from '@mui/material';

import {
    WarningAmber as WarningIcon,
    Gavel as GavelIcon,
    CheckCircle as CheckCircleIcon,
    People as PeopleIcon,
    Chat as ChatIcon,
    VerifiedUser as VerifiedUserIcon,
    Home as HomeIcon,
    EmojiPeople as EmojiPeopleIcon,
    Close as CloseIcon,
    MenuBook as MenuBookIcon,
    ListAlt as ListAltIcon,
    Shield as ShieldIcon,
    TipsAndUpdates as TipsAndUpdatesIcon,
    Forum as ForumIcon
} from '@mui/icons-material';

// スクロール用の関数
const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
        const offset = 80; // ヘッダーの高さ分のオフセット
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;

        window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
        });
    }
};

const PostContent = () => {
    const [showToc, setShowToc] = useState(true);
    return (
        <Box sx={{ maxWidth: 800, mx: 'auto', px: { xs: 1, sm: 3 } }}>
            {/* 目次 */}
            {showToc ? (
                <Card sx={{ mb: 4, boxShadow: 2, bgcolor: '#e3f2fd', border: '1px solid #ddd', borderRadius: 2 }}>
                    <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Typography variant="h6" sx={{ flexGrow: 1 }}>
                                目次
                            </Typography>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<CloseIcon fontSize="small" />}
                                onClick={() => setShowToc(false)}
                            >
                                閉じる
                            </Button>
                        </Box>
                        <List sx={{ p: 0 }}>
                            <ListItem sx={{ p: 0.5, display: 'flex', alignItems: 'center' }}>
                                <Box sx={{ minWidth: 32, minHeight: 32, bgcolor: '#f5f5f5', color: '#888', fontWeight: 'bold', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1, fontSize: '1.1rem' }}>
                                    1.
                                </Box>
                                <Link component="button" onClick={() => scrollToSection('introduction')} color="inherit" underline="hover" sx={{ cursor: 'pointer', fontSize: '0.95rem' }}>
                                    ルームシェアのリアルな現実とは？
                                </Link>
                            </ListItem>
                            <ListItem sx={{ p: 0.5, display: 'flex', alignItems: 'center' }}>
                                <Box sx={{ minWidth: 32, minHeight: 32, bgcolor: '#f5f5f5', color: '#888', fontWeight: 'bold', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1, fontSize: '1.1rem' }}>
                                    2.
                                </Box>
                                <Link component="button" onClick={() => scrollToSection('common-troubles')} color="inherit" underline="hover" sx={{ cursor: 'pointer', fontSize: '0.95rem' }}>
                                    よくある住民トラブル事例
                                </Link>
                            </ListItem>
                            <ListItem sx={{ p: 0.5, display: 'flex', alignItems: 'center' }}>
                                <Box sx={{ minWidth: 32, minHeight: 32, bgcolor: '#f5f5f5', color: '#888', fontWeight: 'bold', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1, fontSize: '1.1rem' }}>
                                    3.
                                </Box>
                                <Link component="button" onClick={() => scrollToSection('solutions')} color="inherit" underline="hover" sx={{ cursor: 'pointer', fontSize: '0.95rem' }}>
                                    トラブル解決のポイント
                                </Link>
                            </ListItem>
                            <ListItem sx={{ p: 0.5, display: 'flex', alignItems: 'center' }}>
                                <Box sx={{ minWidth: 32, minHeight: 32, bgcolor: '#f5f5f5', color: '#888', fontWeight: 'bold', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1, fontSize: '1.1rem' }}>
                                    4.
                                </Box>
                                <Link component="button" onClick={() => scrollToSection('prevention')} color="inherit" underline="hover" sx={{ cursor: 'pointer', fontSize: '0.95rem' }}>
                                    トラブル予防チェックリスト
                                </Link>
                            </ListItem>
                            <ListItem sx={{ p: 0.5, display: 'flex', alignItems: 'center' }}>
                                <Box sx={{ minWidth: 32, minHeight: 32, bgcolor: '#f5f5f5', color: '#888', fontWeight: 'bold', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1, fontSize: '1.1rem' }}>
                                    5.
                                </Box>
                                <Link component="button" onClick={() => scrollToSection('case-studies')} color="inherit" underline="hover" sx={{ cursor: 'pointer', fontSize: '0.95rem' }}>
                                    体験談と専門家アドバイス
                                </Link>
                            </ListItem>
                        </List>
                    </CardContent>
                </Card>
            ) : (
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setShowToc(true)}
                    sx={{ mb: 2 }}
                >
                    目次を表示
                </Button>
            )}

            {/* 導入 */}
            <Box id="introduction" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    <PeopleIcon sx={{ mr: 1, verticalAlign: 'middle', color: 'primary.main' }} />
                    ルームシェアのリアルな現実とは？
                </Typography>
                <Typography variant="body1" paragraph>
                    近年、都心部を中心に「ルームシェア」の人気が急上昇しています。家賃を抑えながら、広い空間や新しい人間関係を楽しめるのが魅力。しかし、共同生活には“楽しい”だけでは語れない現実も。<b>実際に住み始めてから発覚する住民間トラブル</b>に頭を悩ませる方は少なくありません。
                </Typography>
                <Typography variant="body1" paragraph>
                    この記事では、ルームシェアの現場で起こりがちな住民トラブルの実例と、トラブルを上手に乗り切るためのポイント、そして未然に防ぐための知恵を、豊富な事例とともに解説します。<b>「これを知っていれば安心！」</b>と思える内容を、ぜひ最後までご覧ください。
                </Typography>
                <Box sx={{ my: 3, display: 'flex', justifyContent: 'center' }}>
                    <img
                        src="/images/blog/living-dinner_roomshare-trouble-cases.jpg"
                        alt="明るい共有リビングでルームメイト同士が食事や会話を楽しむ様子。木目調のテーブルや観葉植物が配置され、和やかな雰囲気の中で新しい交流が生まれているシーン。"
                        style={{ borderRadius: 10, maxWidth: '100%', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
                    />
                </Box>
            </Box>

            {/* よくある住民トラブル */}
            <Box id="common-troubles" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    <WarningIcon sx={{ mr: 1, verticalAlign: 'middle', color: 'error.main' }} />
                    よくある住民トラブル事例
                </Typography>
                <Typography variant="body1" paragraph>
                    ルームシェア経験者が直面した「あるある」トラブル。ここでは代表的なものをピックアップし、どんな背景で起きやすいかも解説します。
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <ListAltIcon sx={{ mr: 1, color: 'primary.main' }} />
                                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>生活音・騒音問題</Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                                夜遅くのテレビ・音楽や、友人の呼びすぎによる騒音。生活リズムの違いからストレスが蓄積し、思わぬ衝突に発展することも。
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <GavelIcon sx={{ mr: 1, color: 'secondary.main' }} />
                                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>共有スペースの使い方</Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                                キッチンやリビングの掃除当番をさぼったり、私物を放置したり。<b>「片付けない人」</b>がトラブルの火種になることも。
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <ChatIcon sx={{ mr: 1, color: 'primary.light' }} />
                                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>金銭トラブル</Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                                家賃や光熱費の支払い遅延、共用の消耗品費の負担割合など、<b>お金の問題</b>は関係悪化のきっかけになりがちです。
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <ShieldIcon sx={{ mr: 1, color: 'success.main' }} />
                                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>プライバシー侵害</Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                                勝手に部屋に入られたり、私物を使われたり。<b>「一線を越える行動」</b>は信頼関係を大きく損ないます。
                            </Typography>
                        </Paper>
                    </Grid>
                </Grid>
                <Divider sx={{ my: 3 }} />
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                    <WarningIcon sx={{ mr: 0.5, fontSize: 18, color: 'error.main', verticalAlign: 'middle' }} />
                    重要：トラブルは小さな「違和感」から始まることが多いです。早めの対策が肝心！
                </Typography>
                <Box sx={{ my: 3, display: 'flex', justifyContent: 'center' }}>

                </Box>
            </Box>

            {/* 解決策まとめ */}
            <Box id="solutions" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    <TipsAndUpdatesIcon sx={{ mr: 1, verticalAlign: 'middle', color: 'warning.main' }} />
                    トラブル解決のポイント
                </Typography>
                <Typography variant="body1" paragraph>
                    万が一トラブルが発生しても、冷静に対処すれば関係修復は十分可能です。<b>よくあるケース別に、専門家視点から解決策をまとめました。</b>
                </Typography>
                <List>
                    <ListItem>
                        <ListItemIcon>
                            <ChatIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                            primary={<b>話し合いは「感情」ではなく「事実ベース」で</b>}
                            secondary="「うるさい！」と怒るよりも、『昨日23時以降の音量が気になった』と具体的に伝えることで、感情的な対立を避けやすくなります。"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            <VerifiedUserIcon color="success" />
                        </ListItemIcon>
                        <ListItemText
                            primary={<b>ルールや分担表を「見える化」する</b>}
                            secondary="当番や役割分担は紙やアプリで可視化。忘れ防止＆責任の明確化に役立ちます。"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            <GavelIcon color="secondary" />
                        </ListItemIcon>
                        <ListItemText
                            primary={<b>第三者（管理会社やシェア責任者）に相談</b>}
                            secondary="直接解決が難しい場合は、運営者や管理人に早めに相談しましょう。中立的な立場で調整してくれる場合も多いです。"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            <EmojiPeopleIcon color="warning" />
                        </ListItemIcon>
                        <ListItemText
                            primary={<b>「譲れないポイント」と「妥協できるポイント」を整理</b>}
                            secondary="全員が100%満足することは難しいもの。自分の中で優先順位を整理しておくと、冷静な交渉が可能です。"
                        />
                    </ListItem>
                </List>
                <Divider sx={{ my: 3 }} />
                <Box sx={{ bgcolor: '#fffde7', p: 2, borderRadius: 2, border: '1px solid #ffe082', boxShadow: 0, mb: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'warning.main', display: 'flex', alignItems: 'center' }}>
                        <TipsAndUpdatesIcon sx={{ mr: 1 }} />
                        ワンポイントアドバイス
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        トラブル時は「LINEやチャット」など、記録が残るツールでやりとりするのも有効です。後から言った・言わないで揉めるのを防げます。
                    </Typography>
                </Box>
            </Box>

            {/* 予防のポイント */}
            <Box id="prevention" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    <ShieldIcon sx={{ mr: 1, verticalAlign: 'middle', color: 'info.main' }} />
                    トラブル予防チェックリスト
                </Typography>
                <Typography variant="body1" paragraph>
                    トラブルを未然に防ぐには、<b>「最初のルール作り」と「日々の小さな配慮」</b>がカギです。
                </Typography>
                <Box sx={{ mb: 2 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <Card sx={{ height: '100%' }}>
                                <CardContent>
                                    <Typography variant="h6" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                                        <MenuBookIcon sx={{ mr: 1, color: 'primary.main' }} />
                                        事前チェック
                                    </Typography>
                                    <List>
                                        <ListItem sx={{ py: 0 }}>
                                            <ListItemIcon>
                                                <CheckCircleIcon color="success" />
                                            </ListItemIcon>
                                            <ListItemText primary="入居前のルール作りに必ず参加" />
                                        </ListItem>
                                        <ListItem sx={{ py: 0 }}>
                                            <ListItemIcon>
                                                <CheckCircleIcon color="success" />
                                            </ListItemIcon>
                                            <ListItemText primary="ルール・禁止事項を明文化" />
                                        </ListItem>
                                        <ListItem sx={{ py: 0 }}>
                                            <ListItemIcon>
                                                <CheckCircleIcon color="success" />
                                            </ListItemIcon>
                                            <ListItemText primary="生活リズム・価値観のすり合わせ面談" />
                                        </ListItem>
                                    </List>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Card sx={{ height: '100%' }}>
                                <CardContent>
                                    <Typography variant="h6" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                                        <HomeIcon sx={{ mr: 1, color: 'secondary.main' }} />
                                        日々の心がけ
                                    </Typography>
                                    <List>
                                        <ListItem sx={{ py: 0 }}>
                                            <ListItemIcon>
                                                <CheckCircleIcon color="success" />
                                            </ListItemIcon>
                                            <ListItemText primary="気になったことは早めに伝える" />
                                        </ListItem>
                                        <ListItem sx={{ py: 0 }}>
                                            <ListItemIcon>
                                                <CheckCircleIcon color="success" />
                                            </ListItemIcon>
                                            <ListItemText primary="掃除・ごみ出し当番はしっかり守る" />
                                        </ListItem>
                                        <ListItem sx={{ py: 0 }}>
                                            <ListItemIcon>
                                                <CheckCircleIcon color="success" />
                                            </ListItemIcon>
                                            <ListItemText primary="プライバシー・所有物の尊重" />
                                        </ListItem>
                                    </List>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </Box>
                <Divider sx={{ my: 3 }} />
                <Box sx={{ bgcolor: '#e3f2fd', p: 2, borderRadius: 2, border: '1px solid #90caf9', boxShadow: 0 }}>
                    <Typography variant="body2" color="primary" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                        <CheckCircleIcon sx={{ mr: 1 }} />
                        予防のポイントまとめ
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        「ルールは守る」「困ったら相談」「人のモノを勝手に使わない」——この3つを意識するだけで、住民間のトラブルは大きく減らせます。
                    </Typography>
                </Box>
                <Box sx={{ my: 3, display: 'flex', justifyContent: 'center' }}>
                    <img
                        src="/images/blog/sharehouse-rule_roomshare-trouble-cases.jpg"
                        alt="カラフルなホワイトボードにルームシェアのルールや当番表が書かれ、リビングの一角に設置されている様子。住民全員が見える場所に掲示され、誰もがルールを意識できる工夫がなされている。"
                        style={{ borderRadius: 10, maxWidth: '100%', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
                    />
                </Box>
            </Box>

            {/* 体験談 */}
            <Box id="case-studies" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    <ForumIcon sx={{ mr: 1, verticalAlign: 'middle', color: 'secondary.main' }} />
                    体験談と専門家アドバイス
                </Typography>
                <Typography variant="body1" paragraph>
                    実際にルームシェアを経験した方の声を通じて、リアルなトラブルとその乗り越え方を紹介します。<b>「自分にも起こり得る」</b>という視点で、参考にしてください。
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <Avatar src="/images/avatars/case1.jpg" alt="20代女性のルームシェア体験者の顔写真" sx={{ mr: 1 }} />
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Aさん（20代女性）</Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                <b>「生活音のストレスが積もり積もって…」</b>
                            </Typography>
                            <Typography variant="body2">
                                「夜勤明けで寝ているときに、リビングで騒がれて眠れないことが続きました。最初は我慢していたのですが、正直に気持ちを伝えたら相手も配慮してくれるようになり、今は快適に過ごせています。」
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <Avatar src="/images/avatars/case2.jpg" alt="30代男性のルームシェア体験者の顔写真" sx={{ mr: 1 }} />
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Bさん（30代男性）</Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                <b>「お金の話は曖昧にしない！」</b>
                            </Typography>
                            <Typography variant="body2">
                                「家賃を現金手渡しにしていた頃、支払いが遅れる人がいて困りました。管理アプリを導入したことで、お互いの負担がはっきりし、トラブルもなくなりました。」
                            </Typography>
                        </Paper>
                    </Grid>
                </Grid>
                <Divider sx={{ my: 3 }} />
                <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 2, border: '1px solid #ddd', boxShadow: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, display: 'flex', alignItems: 'center', color: 'primary.dark' }}>
                        <VerifiedUserIcon sx={{ mr: 1, color: 'primary.main' }} />
                        専門家からのひと言
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        トラブルの多くは「コミュニケーション不足」が原因です。<b>違和感を我慢せず、早めに小さな相談を重ねる</b>ことが、快適なシェア生活への近道。自分も相手も「変化する」ことを受け入れ、柔軟に対応しましょう。
                    </Typography>
                </Box>
                <Box sx={{ my: 3, display: 'flex', justifyContent: 'center' }}>
                    <img
                        src="/images/blog/sharehouse-communication_roomshare-trouble-cases.jpg"
                        alt="共有スペースのテーブルでルームメイト数人がリラックスして会話し、笑顔でコミュニケーションをとっている様子。温かい照明の中、親しみやすい雰囲気が伝わるシーン。"
                        style={{ borderRadius: 10, maxWidth: '100%', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
                    />
                </Box>
            </Box>

            {/* 総まとめ */}
            <Box sx={{ mb: 6 }}>
                <Divider sx={{ my: 3 }} />
                <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2 }}>
                    まとめ：ルームシェア成功のカギは「小さな気配り」と「対話」
                </Typography>
                <Typography variant="body1">
                    ルームシェアは、生活コストを抑えつつ多様な出会いや経験を得られる素晴らしい選択肢です。しかし、共同生活ならではのトラブルも避けて通れません。<b>「困ったときは早めに相談」</b>、「ルールと配慮を大切に」——この2点を忘れず、快適なシェアライフを楽しんでください。
                </Typography>
            </Box>
        </Box>
    );
};

const post = {
    id: 'roomshare-trouble-cases',
    slug: 'roomshare-trouble-cases',
    title: 'ルームシェアの「住民トラブル」事例集',
    excerpt: 'よくある住民トラブル 解決策まとめ 予防のポイント 体験談',
    coverImage: '/images/blog/cover-roomshare-trouble_roomshare-trouble-cases.jpg',
    coverImageAlt: '明るい自然光が差し込むモダンなルームシェアの共有リビングで、多様な背景を持つルームメイトたちが楽しく会話をしている様子。部屋全体が温かく和やかな雰囲気に包まれており、協力的な共同生活の魅力が感じられる。',
    publishedAt: '2024-06-22T00:00:00+09:00',
    isPublished: true,
    categories: ['ルームシェア', '住民トラブル', '暮らしの知恵'],
    tags: ['ルームシェア', '住民トラブル', 'トラブル解決', '予防策', '体験談'],
    author: {
        name: '伊藤 未来',
        avatar: '/images/avatars/ito-mirai.jpg',
        bio: 'シェアハウスコンサルタント・ファシリテーター。10年以上にわたり、都市部でのルームシェア運営・相談実績多数。快適な共同生活のためのノウハウ発信に尽力中。',
        profilePicture: '/images/avatars/ito-mirai.jpg'
    },
    viewCount: 1240,
    contentComponent: PostContent
};

export default post;